/**
 * User Presence Status Hook
 * - Tracks active/away/on_break/busy/offline status
 * - Auto-away after 15 minutes of inactivity
 * - Heartbeat every 60 seconds
 * - Status change API calls
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { API } from '@/App';
import axios from 'axios';

const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const HEARTBEAT_INTERVAL = 60 * 1000; // 60 seconds
const STATUS_POLL_INTERVAL = 30 * 1000; // 30 seconds

const STATUS_CONFIG = {
  active:   { color: 'bg-green-500',  label: 'Active',   ringColor: 'ring-green-500/30' },
  away:     { color: 'bg-yellow-500', label: 'Away',     ringColor: 'ring-yellow-500/30' },
  on_break: { color: 'bg-orange-500', label: 'On Break', ringColor: 'ring-orange-500/30' },
  busy:     { color: 'bg-red-500',    label: 'Busy',     ringColor: 'ring-red-500/30' },
  offline:  { color: 'bg-slate-400',  label: 'Offline',  ringColor: 'ring-slate-400/30' },
};

export function useUserStatus() {
  const [myStatus, setMyStatus] = useState('offline');
  const [teamStatuses, setTeamStatuses] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  const idleTimerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const teamPollRef = useRef(null);
  const wasAutoAway = useRef(false);
  const tokenRef = useRef(null);

  // Set initial token
  useEffect(() => {
    tokenRef.current = localStorage.getItem('token');
  }, []);

  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${tokenRef.current}`,
    'Content-Type': 'application/json',
  }), []);

  // Update status on the server
  const updateStatus = useCallback(async (newStatus) => {
    try {
      await axios.put(`${API}/user-status`, { status: newStatus }, { headers: getHeaders() });
      setMyStatus(newStatus);
    } catch (err) {
      console.error('Status update failed:', err);
    }
  }, [getHeaders]);

  // Heartbeat
  const sendHeartbeat = useCallback(async () => {
    try {
      await axios.post(`${API}/user-status/heartbeat`, {}, { headers: getHeaders() });
    } catch { /* ignore */ }
  }, [getHeaders]);

  // Fetch team statuses
  const fetchTeamStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user-status`, { headers: getHeaders() });
      setTeamStatuses(res.data || []);
    } catch { /* ignore */ }
  }, [getHeaders]);

  // Reset idle timer (called on user activity)
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    // If user was auto-set to away, restore to active
    if (wasAutoAway.current && myStatus === 'away') {
      wasAutoAway.current = false;
      updateStatus('active');
    }

    idleTimerRef.current = setTimeout(() => {
      // Only auto-away if currently active
      if (myStatus === 'active') {
        wasAutoAway.current = true;
        updateStatus('away');
      }
    }, IDLE_TIMEOUT);
  }, [myStatus, updateStatus]);

  // Set up idle detection, heartbeat, and team polling
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    tokenRef.current = token;

    // Set active on mount
    updateStatus('active');

    // Listen for user activity events
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    let lastActivity = Date.now();
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastActivity > 5000) {  // Throttle to every 5 seconds
        lastActivity = now;
        resetIdleTimer();
      }
    };
    activityEvents.forEach(e => window.addEventListener(e, throttledReset, { passive: true }));

    // Start idle timer
    resetIdleTimer();

    // Heartbeat
    heartbeatRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    // Team status polling
    fetchTeamStatus();
    teamPollRef.current = setInterval(fetchTeamStatus, STATUS_POLL_INTERVAL);

    return () => {
      activityEvents.forEach(e => window.removeEventListener(e, throttledReset));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (teamPollRef.current) clearInterval(teamPollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set offline on tab close (best-effort, heartbeat timeout is the reliable fallback)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && myStatus === 'active') {
        // Tab hidden — will go to "away" via idle timer or heartbeat timeout
      } else if (!document.hidden && myStatus !== 'busy' && myStatus !== 'on_break') {
        // Tab visible again — restore active
        updateStatus('active');
        sendHeartbeat();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [myStatus, updateStatus, sendHeartbeat]);

  return {
    myStatus,
    teamStatuses,
    showPicker,
    setShowPicker,
    updateStatus,
    STATUS_CONFIG,
  };
}
